import React, { useCallback, useEffect, useState } from 'react';
import { ListChecks, Scale, Sparkles } from 'lucide-react';
import { adminApi } from '../../services/api';
import { EtatErreur } from '../../etats';
import { TableauResultats } from '../resultats/Resultats';
import { Carte, Chargement, TexteLignes, Vide } from './ui';

/* Le détail d'un domaine pour le coach : chaque sous-domaine, son niveau,
   ce qui est acquis, ce qui bloque, ce qu'il faut faire. */
function DetailDomaine({ domaine }) {
  return (
    <ul className="space-y-3">
      {domaine.sous_domaines.map((sd) => (
        <li key={sd.nom} className="bg-[var(--bg-main)] rounded-pastille p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <span className="text-sm font-semibold text-[var(--text-bold)]">{sd.nom}</span>
            <span className="text-xs text-[var(--text-muted)] tabular-nums">
              niveau {sd.niveau_referentiel ?? '—'} / 5 · {sd.score ?? '—'} %
            </span>
          </div>
          {sd.force && <p className="text-xs text-[var(--vert-fonce)] mt-1"><span className="font-semibold">Acquis : </span>{sd.force}</p>}
          {sd.faiblesse && <p className="text-xs text-[var(--alerte)] mt-1"><span className="font-semibold">Point bloquant : </span>{sd.faiblesse}</p>}
          {sd.action && <TexteLignes texte={sd.action} className="text-xs text-[var(--text-main)] mt-1" />}
          {sd.livrables && <TexteLignes texte={`Livrables attendus :\n${sd.livrables}`} className="text-xs text-[var(--text-muted)] mt-1" />}
        </li>
      ))}
    </ul>
  );
}

/* Lecture automatique d'un diagnostic : synthese, domaines, forces et
   faiblesses tirees du referentiel, actions correctives proposees. */
export default function LectureDiagnostic({ diagnostic }) {
  const [lecture, setLecture] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [essai, setEssai] = useState(0);
  const reessayer = useCallback(() => setEssai((n) => n + 1), []);

  useEffect(() => {
    let vivant = true;
    setLecture(null);
    setErreur(null);
    adminApi.getInterpretation(diagnostic.id)
      .then((d) => vivant && setLecture(d))
      .catch((e) => vivant && setErreur(e));
    return () => { vivant = false; };
  }, [diagnostic.id, essai]);

  if (erreur) return <EtatErreur erreur={erreur} onReessayer={reessayer} />;
  if (!lecture) return <Chargement forme="texte" />;
  if (!lecture.compteurs.reponses) {
    return <Vide icone={ListChecks} titre="Pas encore de réponses" texte="La lecture s'affichera dès que l'association ou le coach aura répondu au questionnaire." />;
  }

  return (
    <TableauResultats
      score={lecture.score_global}
      domaines={lecture.domaines.map((d) => ({
        ...d,
        precision: d.score_association != null && d.score_expert != null
          ? `association ${d.score_association} % / coach ${d.score_expert} %` : '',
      }))}
      detailDomaine={(d) => <DetailDomaine domaine={d} />}
      restitution={(
        <>
          <p className="font-semibold text-[var(--text-bold)] flex items-start gap-2">
            <Sparkles className="w-4 h-4 mt-1 shrink-0 text-[var(--accent-texte)]" />{lecture.resume}
          </p>
          {lecture.synthese && <p>{lecture.synthese}</p>}
          <p className="text-xs text-[var(--text-muted)]">
            {lecture.compteurs.dirigeants} dirigeant(s), {lecture.compteurs.membres} membre(s) et {lecture.compteurs.coach} coach ont répondu.
          </p>
        </>
      )}
      forces={lecture.forces.map((f) => ({ titre: f.sous_domaine, description: f.description }))}
      faiblesses={lecture.faiblesses.map((f) => ({ titre: f.sous_domaine, description: f.description }))}
      apres={lecture.ecarts.length > 0 && (
        <Carte titre={<span className="inline-flex items-center gap-2"><Scale className="w-4 h-4 text-[var(--attention)]" />Écarts de perception</span>}>
          <ul className="space-y-2 text-sm text-[var(--text-main)]">
            {lecture.ecarts.map((e) => (
              <li key={e.sous_domaine}>
                <span className="font-semibold text-[var(--text-bold)]">{e.sous_domaine}</span> : l'association se place au niveau {e.niveau_association}, le coach au niveau {e.niveau_expert}.
              </li>
            ))}
          </ul>
        </Carte>
      )}
      // Le plan d'actions, c'est elles : elles y entrent seules à la clôture
      // du diagnostic (`generer_plan`), sans bouton à presser.
      actions={lecture.actions.map((a) => ({
        description: a.description, priorite: a.priorite, domaine: a.domaine,
        sous_domaine: a.sous_domaine, niveau: a.niveau, livrables: a.livrables,
        mention: a.depuis_referentiel ? '' : 'action générique',
      }))}
      introPlan={diagnostic.statut === 'cloture'
        ? "Elles forment le plan d'actions du projet (onglet « Plan d'actions »), où l'on suit leur avancement."
        : "Elles entreront automatiquement dans le plan d'actions à la clôture du diagnostic."}
      videPlan="Tous les sous-domaines sont au niveau 5 : rien à corriger."
    />
  );
}
